import { CHARACTER_DRAFT_SCHEMA_VERSION } from '../../../domain/types.js';
import type {
  CharacterBasic,
  CharacterDraft,
  CharacterNature,
  CharacterAppearance,
  LearnedTrait,
  WorldDraft,
} from '../../../domain/types.js';
import { localUid, bodyFromCharacterDraft, type CharacterBodySnapshot } from '../characterBody.js';
import { MAX_CHARACTERS } from './defaults.js';
import { rollVisits } from '../../../runtime/visitPresence.js';

export { localUid };

export function activeCharacters(draft: WorldDraft): CharacterDraft[] {
  return draft.characterDrafts.filter((c) => c.status !== 'removed');
}

export function addCharacterToDraft(
  draft: WorldDraft,
  partial: Pick<CharacterDraft, 'basic' | 'appearance' | 'nature' | 'traits' | 'source'> &
    Partial<Pick<CharacterDraft, 'sex' | 'species' | 'background' | 'ageRange' | 'draftCharacterId' | 'visits' | 'visitor'>>,
): WorldDraft {
  if (activeCharacters(draft).length >= MAX_CHARACTERS) return draft;
  const now = new Date().toISOString();
  const character: CharacterDraft = {
    draftCharacterId: partial.draftCharacterId ?? localUid('char'),
    schemaVersion: CHARACTER_DRAFT_SCHEMA_VERSION,
    basic: partial.basic,
    sex: partial.sex,
    species: partial.species,
    background: partial.background,
    ageRange: partial.ageRange,
    appearance: partial.appearance,
    nature: partial.nature,
    traits: partial.traits,
    status: 'draft',
    source: partial.source,
    dependencyIds: [],
    updatedAt: now,
    visits: partial.visits,
    visitor: partial.visitor,
  };
  return { ...draft, updatedAt: now, characterDrafts: [...draft.characterDrafts, character] };
}

export function patchCharacterInDraft(
  draft: WorldDraft,
  draftCharacterId: string,
  patch: Partial<Pick<CharacterDraft, 'basic' | 'appearance' | 'nature' | 'traits' | 'status' | 'source' | 'sex' | 'species' | 'background' | 'ageRange' | 'visits' | 'visitor'>>,
): WorldDraft {
  const now = new Date().toISOString();
  return {
    ...draft,
    updatedAt: now,
    characterDrafts: draft.characterDrafts.map((c) =>
      c.draftCharacterId === draftCharacterId ? { ...c, ...patch, updatedAt: now } : c,
    ),
  };
}

export function removeCharacterFromDraft(draft: WorldDraft, draftCharacterId: string): WorldDraft {
  return patchCharacterInDraft(draft, draftCharacterId, { status: 'removed' });
}

export function newWorkingCharacter(source: CharacterDraft['source'] = 'user'): CharacterDraft {
  const now = new Date().toISOString();
  return {
    draftCharacterId: localUid('char'),
    schemaVersion: CHARACTER_DRAFT_SCHEMA_VERSION,
    basic: { firstName: '', lastInitial: undefined },
    sex: 'M',
    species: 'Human',
    ageRange: 'Adult',
    appearance: null,
    nature: null,
    traits: [],
    status: 'draft',
    source,
    dependencyIds: [],
    updatedAt: now,
  };
}

export function mergeWorking(
  base: CharacterDraft,
  patch: {
    basic?: CharacterBasic;
    appearance?: CharacterAppearance | null;
    nature?: CharacterNature | null;
    traits?: LearnedTrait[];
    sex?: CharacterDraft['sex'];
    species?: string;
    background?: string;
    ageRange?: string;
    visits?: CharacterDraft['visits'];
    visitor?: boolean;
    source?: CharacterDraft['source'];
  },
): CharacterDraft {
  return {
    ...base,
    ...patch,
    basic: patch.basic ?? base.basic,
    appearance: patch.appearance !== undefined ? patch.appearance : base.appearance,
    nature: patch.nature !== undefined ? patch.nature : base.nature,
    traits: patch.traits ?? base.traits,
    sex: patch.sex ?? base.sex,
    species: patch.species ?? base.species,
    background: patch.background ?? base.background,
    ageRange: patch.ageRange ?? base.ageRange,
    visits: patch.visits ?? base.visits,
    visitor: patch.visitor ?? base.visitor,
    updatedAt: new Date().toISOString(),
  };
}

function appearanceFromBody(body: CharacterBodySnapshot): CharacterDraft['appearance'] {
  if (!body.appearanceStyles) return null;
  return {
    skinColor: body.palette.skin,
    topStyle: body.appearanceStyles.topStyle,
    topColor: body.palette.shirt,
    bottomStyle: body.appearanceStyles.bottomStyle,
    bottomColor: body.palette.pants,
    hairStyle: body.appearanceStyles.hairStyle,
    hairColor: body.palette.hair,
  };
}

function traitsFromBody(body: CharacterBodySnapshot): LearnedTrait[] {
  return body.nurture.map((t) => ({
    traitKey: t.trait_key,
    strength: t.strength,
    reason: t.reason,
  }));
}

/** Write every character field into the world draft and a matching body sidecar. */
export function withSavedCharacterFields(
  draft: WorldDraft,
  bodies: Record<string, CharacterBodySnapshot>,
  working: CharacterDraft | null,
): { draft: WorldDraft; bodies: Record<string, CharacterBodySnapshot> } {
  let nextDraft = draft;
  if (working?.basic.firstName.trim()) {
    const id = working.draftCharacterId;
    const exists = nextDraft.characterDrafts.some((c) => c.draftCharacterId === id);
    nextDraft = exists
      ? patchCharacterInDraft(nextDraft, id, {
        basic: working.basic,
        appearance: working.appearance,
        nature: working.nature,
        traits: working.traits,
        sex: working.sex,
        species: working.species,
        background: working.background,
        ageRange: working.ageRange,
        visits: working.visits,
        visitor: working.visitor,
        source: working.source,
      })
      : addCharacterToDraft(nextDraft, {
        draftCharacterId: id,
        basic: working.basic,
        appearance: working.appearance,
        nature: working.nature,
        traits: working.traits,
        sex: working.sex,
        species: working.species,
        background: working.background,
        ageRange: working.ageRange,
        visits: working.visits,
        visitor: working.visitor,
        source: working.source,
      });
  }

  const nextBodies: Record<string, CharacterBodySnapshot> = { ...bodies };
  const characterDrafts = nextDraft.characterDrafts.map((character) => {
    if (character.status === 'removed') return character;
    const body = nextBodies[character.draftCharacterId];
    const saved: CharacterDraft = {
      ...character,
      sex: character.sex ?? body?.sex ?? 'M',
      species: character.species ?? body?.species ?? 'Human',
      ageRange: character.ageRange ?? body?.ageRange ?? 'Adult',
      appearance: character.appearance ?? (body ? appearanceFromBody(body) : null),
      nature: character.nature ?? (body ? { ...body.nature } : null),
      traits: character.traits.length > 0 ? character.traits : (body ? traitsFromBody(body) : []),
      visits: character.visits ? rollVisits(character.visits) : undefined,
    };
    const rebuilt = bodyFromCharacterDraft(saved, saved.species, saved.sex);
    if (rebuilt) nextBodies[saved.draftCharacterId] = rebuilt;
    else if (body) {
      nextBodies[saved.draftCharacterId] = {
        ...body,
        sex: saved.sex ?? body.sex,
        species: saved.species ?? body.species,
        ageRange: saved.ageRange ?? body.ageRange,
      };
    }
    return saved;
  });

  return {
    draft: { ...nextDraft, characterDrafts },
    bodies: nextBodies,
  };
}
